import React, { createContext, useContext, useRef, useEffect, useCallback, useState } from 'react';
import { trackFlashcardSession, trackEvent } from '../lib/analytics';

const TimeTrackingContext = createContext(null);

// Features that should be tracked
const TRACKED_FEATURES = {
  flashcards: { name: 'Flashcards', category: 'study' },
  quizzes: { name: 'Quizzes', category: 'practice' },
  exams: { name: 'Exams', category: 'practice' },
  studyquiz: { name: 'Study Content', category: 'study' },
  lectures: { name: 'Lectures', category: 'study' },
  cards: { name: 'Flashcards', category: 'study' }, // Alias
};

// Idle timeout in ms (60 seconds)
const IDLE_TIMEOUT = 60000;

// Minimum session length to log (in seconds) - don't log very short sessions
const MIN_SESSION_LENGTH = 5;

/**
 * TimeTrackingProvider - Tracks active time spent on features
 * Uses Page Visibility API + Activity Detection for accuracy
 */
export function TimeTrackingProvider({ children, userId, onSessionLog }) {
  const [currentFeature, setCurrentFeature] = useState(null);
  const [isTracking, setIsTracking] = useState(false);
  const [activeSeconds, setActiveSeconds] = useState(0);
  const [isIdle, setIsIdle] = useState(false);

  // Refs for tracking state without re-renders
  const activeSecondsRef = useRef(0);
  const lastActivityRef = useRef(Date.now());
  const isVisibleRef = useRef(!document.hidden);
  const isIdleRef = useRef(false);
  const featureStartTimeRef = useRef(null);
  const currentFeatureRef = useRef(null);
  const userIdRef = useRef(userId);
  const sessionDataRef = useRef({}); // Store additional session data (e.g., cards viewed)

  // Update userId ref
  useEffect(() => {
    userIdRef.current = userId;
  }, [userId]);

  // Activity detection
  useEffect(() => {
    const handleActivity = () => {
      lastActivityRef.current = Date.now();
      if (isIdleRef.current) {
        isIdleRef.current = false;
        setIsIdle(false);
      }
    };

    const events = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'click'];
    events.forEach(e => window.addEventListener(e, handleActivity, { passive: true }));

    return () => {
      events.forEach(e => window.removeEventListener(e, handleActivity));
    };
  }, []);

  // Visibility detection
  useEffect(() => {
    const handleVisibility = () => {
      const visible = !document.hidden;
      isVisibleRef.current = visible;
      if (visible) {
        lastActivityRef.current = Date.now();
      }
    };

    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, []);

  // Timer tick
  useEffect(() => {
    if (!isTracking) return;

    const interval = setInterval(() => {
      const now = Date.now();
      const timeSinceActivity = now - lastActivityRef.current;
      const userIsIdle = timeSinceActivity >= IDLE_TIMEOUT;

      if (userIsIdle !== isIdleRef.current) {
        isIdleRef.current = userIsIdle;
        setIsIdle(userIsIdle);
      }

      // Only count if visible and active
      if (isVisibleRef.current && !userIsIdle) {
        activeSecondsRef.current += 1;
        setActiveSeconds(activeSecondsRef.current);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [isTracking]);

  // Log session to analytics
  const logSession = useCallback((feature, seconds, extraData = {}) => {
    if (seconds < MIN_SESSION_LENGTH) {
      console.log(`[TimeTracking] Session too short (${seconds}s), not logging`);
      return;
    }

    const featureInfo = TRACKED_FEATURES[feature];
    if (!featureInfo) return;

    const sessionData = {
      feature,
      featureName: featureInfo.name,
      category: featureInfo.category,
      activeSeconds: seconds,
      activeMinutes: Math.round(seconds / 60 * 10) / 10, // 1 decimal place
      userId: userIdRef.current,
      timestamp: new Date().toISOString(),
      ...extraData,
    };

    console.log(`[TimeTracking] Logging session:`, sessionData);

    // Track to Google Analytics
    if (feature === 'flashcards' || feature === 'cards') {
      trackFlashcardSession(
        extraData.subject || 'All',
        extraData.cardsViewed || 0,
        seconds
      );
    } else {
      trackEvent(`${feature}_session`, {
        duration_seconds: seconds,
        duration_minutes: Math.round(seconds / 60 * 10) / 10,
        ...extraData,
      });
    }

    // Call custom callback if provided
    onSessionLog?.(sessionData);
  }, [onSessionLog]);

  // Start tracking a feature
  const startTracking = useCallback((feature) => {
    // If already tracking a different feature, log the previous session
    if (currentFeatureRef.current && currentFeatureRef.current !== feature) {
      logSession(currentFeatureRef.current, activeSecondsRef.current, sessionDataRef.current);
    }

    // Start new session
    currentFeatureRef.current = feature;
    setCurrentFeature(feature);
    activeSecondsRef.current = 0;
    setActiveSeconds(0);
    featureStartTimeRef.current = Date.now();
    lastActivityRef.current = Date.now();
    sessionDataRef.current = {};
    setIsTracking(true);

    console.log(`[TimeTracking] Started tracking: ${feature}`);
  }, [logSession]);

  // Stop tracking and log session
  const stopTracking = useCallback(() => {
    if (currentFeatureRef.current && activeSecondsRef.current > 0) {
      logSession(currentFeatureRef.current, activeSecondsRef.current, sessionDataRef.current);
    }

    currentFeatureRef.current = null;
    setCurrentFeature(null);
    activeSecondsRef.current = 0;
    setActiveSeconds(0);
    featureStartTimeRef.current = null;
    sessionDataRef.current = {};
    setIsTracking(false);

    console.log(`[TimeTracking] Stopped tracking`);
  }, [logSession]);

  // Update session data (e.g., increment cards viewed)
  const updateSessionData = useCallback((data) => {
    sessionDataRef.current = { ...sessionDataRef.current, ...data };
  }, []);

  // Increment a counter in session data
  const incrementSessionCounter = useCallback((key) => {
    sessionDataRef.current[key] = (sessionDataRef.current[key] || 0) + 1;
  }, []);

  // Handle page unload - log final session
  useEffect(() => {
    const handleUnload = () => {
      if (currentFeatureRef.current && activeSecondsRef.current >= MIN_SESSION_LENGTH) {
        // Use sendBeacon for reliable logging on page unload
        const data = {
          feature: currentFeatureRef.current,
          activeSeconds: activeSecondsRef.current,
          userId: userIdRef.current,
          timestamp: new Date().toISOString(),
          ...sessionDataRef.current,
        };

        // Try to send via beacon (works during page unload)
        if (navigator.sendBeacon) {
          const blob = new Blob([JSON.stringify(data)], { type: 'application/json' });
          navigator.sendBeacon('/api/analytics/session', blob);
        }
      }
    };

    window.addEventListener('beforeunload', handleUnload);
    return () => window.removeEventListener('beforeunload', handleUnload);
  }, []);

  // Format time as MM:SS or HH:MM:SS
  const getFormattedTime = useCallback(() => {
    const hours = Math.floor(activeSeconds / 3600);
    const minutes = Math.floor((activeSeconds % 3600) / 60);
    const seconds = activeSeconds % 60;

    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    }
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }, [activeSeconds]);

  const value = {
    // State
    currentFeature,
    isTracking,
    activeSeconds,
    activeMinutes: Math.floor(activeSeconds / 60),
    formattedTime: getFormattedTime(),
    isIdle,

    // Controls
    startTracking,
    stopTracking,
    updateSessionData,
    incrementSessionCounter,

    // Utility
    getFormattedTime,
  };

  return (
    <TimeTrackingContext.Provider value={value}>
      {children}
    </TimeTrackingContext.Provider>
  );
}

/**
 * Hook to use time tracking context
 */
export function useTimeTracking() {
  const context = useContext(TimeTrackingContext);
  if (!context) {
    // Return no-op functions if not wrapped in provider
    return {
      currentFeature: null,
      isTracking: false,
      activeSeconds: 0,
      activeMinutes: 0,
      formattedTime: '0:00',
      isIdle: false,
      startTracking: () => {},
      stopTracking: () => {},
      updateSessionData: () => {},
      incrementSessionCounter: () => {},
      getFormattedTime: () => '0:00',
    };
  }
  return context;
}

/**
 * Hook that automatically tracks time when activeTab changes
 * Use this in the main app component
 */
export function useAutoTimeTracking(activeTab) {
  const { startTracking, stopTracking } = useTimeTracking();
  const previousTabRef = useRef(null);

  useEffect(() => {
    // Check if this is a tracked feature
    if (TRACKED_FEATURES[activeTab]) {
      startTracking(activeTab);
    } else if (previousTabRef.current && TRACKED_FEATURES[previousTabRef.current]) {
      // Switching from a tracked feature to a non-tracked one
      stopTracking();
    }

    previousTabRef.current = activeTab;
  }, [activeTab, startTracking, stopTracking]);

  // Stop tracking on unmount
  useEffect(() => {
    return () => {
      stopTracking();
    };
  }, [stopTracking]);
}

export default TimeTrackingContext;
